import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance.ts';
import { VOICE_IDENTIFICATION_URL } from '@utils/api/endpoints.voice-profiles.ts';
import { VoiceTimelineSpan } from '@utils/types/organization/voice-profile.ts';
import { VOICE_PROFILE_QUERY_KEYS } from '@utils/api/organizations/speakers/voice-profiles/query-keys.ts';

/** Original diarization labels mapped to source-audio milliseconds.
 *
 * Identity decisions are keyed on these original labels, never on display
 * names — the user may have renamed a speaker already, and a rename must not
 * move an identity assignment. The spans are also how an editor row finds its
 * label: both editors time rows in milliseconds, same as this response.
 *
 * A document with no source diarization answers 409
 * (`voice-source-results-unavailable`); callers show an explanatory
 * unavailable state rather than an empty selection flow. */
export const useVoiceIdentificationTimeline = (
  sessionId: string | undefined,
  documentId: string | undefined,
  enabled = true
) =>
  useQuery<VoiceTimelineSpan[], AxiosError>(
    VOICE_PROFILE_QUERY_KEYS.timeline(sessionId ?? '', documentId ?? ''),
    async () => {
      const { data } = await Axios.get<VoiceTimelineSpan[]>(
        VOICE_IDENTIFICATION_URL.timeline(
          sessionId as string,
          documentId as string
        )
      );
      return data;
    },
    {
      enabled: enabled && !!sessionId && !!documentId,
      // Source diarization is fixed once the session has finished.
      staleTime: Infinity,
      retry: false,
      useErrorBoundary: false
    }
  );
